/**
 * Urban Inventory Store — Zustand
 *
 * @remarks
 * Estado global del módulo urban-inventory.
 * Gestiona filtros, selección de entidad y visibilidad de capa.
 * No incluye estado de pinceles ni sesiones (Fase 2B+).
 *
 * @module urban-inventory/store
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { UrbanEntity, UrbanEntityFilters, UrbanEntityStatus } from '../types/urban-entity.types';

// ─── State ────────────────────────────────────────────────────────────────────

export interface UrbanInventoryState {
  /** Tipos de entidad activos (vacío = todos) */
  activeEntityTypes: string[];

  /** Filtro de estado (null = todos) */
  statusFilter: UrbanEntityStatus | null;

  /** ID de la entidad seleccionada (null = ninguna) */
  selectedEntityId: string | null;

  /** Entidad seleccionada completa (null = ninguna) */
  selectedEntity: UrbanEntity | null;

  /** Visibilidad de la capa de mapa */
  layerVisible: boolean;

  /** Mostrar líneas de asociación NPA en el mapa */
  showAssociationLines: boolean;

  /** ID de la entidad bajo hover */
  hoveredEntityId: string | null;
}

// ─── Actions ──────────────────────────────────────────────────────────────────

export interface UrbanInventoryActions {
  setActiveEntityTypes: (types: string[]) => void;
  toggleEntityType: (type: string) => void;
  setStatusFilter: (status: UrbanEntityStatus | null) => void;
  selectEntity: (entity: UrbanEntity | null) => void;
  setHoveredEntity: (id: string | null) => void;
  setLayerVisible: (visible: boolean) => void;
  setShowAssociationLines: (show: boolean) => void;
  reset: () => void;

  /** Devuelve los filtros actuales como objeto para el API service */
  getApiFilters: () => UrbanEntityFilters;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: UrbanInventoryState = {
  activeEntityTypes: [],
  statusFilter: null,
  selectedEntityId: null,
  selectedEntity: null,
  layerVisible: true,
  showAssociationLines: false,
  hoveredEntityId: null,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export type UrbanInventoryStore = UrbanInventoryState & UrbanInventoryActions;

export const useUrbanInventoryStore = create<UrbanInventoryStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setActiveEntityTypes: (types) => set({ activeEntityTypes: types }),

      toggleEntityType: (type) =>
        set((state) => {
          const current = state.activeEntityTypes;
          const next = current.includes(type)
            ? current.filter((t) => t !== type)
            : [...current, type];
          return { activeEntityTypes: next };
        }),

      setStatusFilter: (status) => set({ statusFilter: status }),

      selectEntity: (entity) =>
        set({
          selectedEntity: entity,
          selectedEntityId: entity?.id ?? null,
        }),

      setHoveredEntity: (id) => set({ hoveredEntityId: id }),

      setLayerVisible: (visible) => set({ layerVisible: visible }),

      setShowAssociationLines: (show) => set({ showAssociationLines: show }),

      reset: () => set(initialState),

      getApiFilters: (): UrbanEntityFilters => {
        const { activeEntityTypes, statusFilter } = get();
        const filters: UrbanEntityFilters = {};
        // Solo un entityType a la vez en la API; si hay varios, no filtrar por tipo
        if (activeEntityTypes.length === 1) {
          filters.entityType = activeEntityTypes[0];
        }
        if (statusFilter) {
          filters.status = statusFilter;
        }
        return filters;
      },
    }),
    { name: 'urban-inventory-store' }
  )
);

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectSelectedEntity = (s: UrbanInventoryStore): UrbanEntity | null =>
  s.selectedEntity;
export const selectLayerVisible = (s: UrbanInventoryStore): boolean => s.layerVisible;
export const selectHoveredEntityId = (s: UrbanInventoryStore): string | null => s.hoveredEntityId;
export const selectActiveEntityTypes = (s: UrbanInventoryStore): string[] => s.activeEntityTypes;
